/**
 * Admin Note Templates API
 *
 * Enhanced RTK Query endpoints for admin note template management.
 * Provides cache management and user-friendly hook exports.
 */

import { enhancedApi } from '@store/generated/templates';
import { baseApi } from '@store/baseApi';

// ============================================================================
// Types for Template Revision History (manually defined until OpenAPI updated)
// ============================================================================

/** Summary info for a revision in the history list */
export type RevisionSummary = {
  version: number;
  title: string;
  created_at: string;
  created_by: string;
  section_count: number;
};

/** Response for listing all revisions of a template */
export type AdminListRevisionsResponse = {
  revisions: RevisionSummary[];
};

/** Full revision content (same structure as NoteTemplateRevision2) */
export type RevisionDetail = {
  version: number;
  title: string;
  description: string | null;
  prompt: string | null;
  content: string;
  sections: {
    id: string;
    title: string;
    content: string;
    order?: number;
    example_output?: string | null;
    legacy_module_id?: string | null;
  }[];
  models: string[] | null;
  created_at: string;
  created_by: string;
};

/** Response for getting a specific revision */
export type AdminGetRevisionResponse = RevisionDetail;

// ============================================================================
// Revision History API endpoints (injected into baseApi)
// ============================================================================

const revisionHistoryApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // List all revisions for a template
    getAdminTemplateRevisions: build.query<
      AdminListRevisionsResponse,
      { templateId: string }
    >({
      query: ({ templateId }) => ({
        url: `/v2/admin/notetemplates/${templateId}/revisions`,
      }),
    }),

    // Get a specific revision by version
    getAdminTemplateRevision: build.query<
      AdminGetRevisionResponse,
      { templateId: string; version: number }
    >({
      query: ({ templateId, version }) => ({
        url: `/v2/admin/notetemplates/${templateId}/revisions/${version}`,
      }),
    }),
  }),
  overrideExisting: false,
});

// Export revision history hooks
export const {
  useGetAdminTemplateRevisionsQuery,
  useLazyGetAdminTemplateRevisionsQuery,
  useGetAdminTemplateRevisionQuery,
  useLazyGetAdminTemplateRevisionQuery,
} = revisionHistoryApi;

const NOTE_TEMPLATE_TAG_TYPES = ['NoteTemplate', 'NoteTemplateList'] as const;

export const noteTemplatesAdminApi = enhancedApi.enhanceEndpoints({
  addTagTypes: NOTE_TEMPLATE_TAG_TYPES,
  endpoints: {
    // List all templates with scope filter
    getV2AdminNotetemplates: {
      providesTags: (result) =>
        result
          ? [
              ...(result.templates?.map(t => ({ type: 'NoteTemplate' as const, id: t.id })) || []),
              { type: 'NoteTemplateList' as const, id: 'ALL' },
            ]
          : [{ type: 'NoteTemplateList' as const, id: 'ALL' }],
    },

    // List user templates
    getV2AdminNotetemplatesUserByUserId: {
      providesTags: (result, _error, { userId }) =>
        result
          ? [
              ...(result.templates?.map(t => ({ type: 'NoteTemplate' as const, id: t.id })) || []),
              { type: 'NoteTemplateList' as const, id: `USER_${userId}` },
            ]
          : [{ type: 'NoteTemplateList' as const, id: `USER_${userId}` }],
    },

    // List org templates
    getV2AdminNotetemplatesOrgByOrganizationId: {
      providesTags: (result, _error, { organizationId }) =>
        result
          ? [
              ...(result.templates?.map(t => ({ type: 'NoteTemplate' as const, id: t.id })) || []),
              { type: 'NoteTemplateList' as const, id: `ORG_${organizationId}` },
            ]
          : [{ type: 'NoteTemplateList' as const, id: `ORG_${organizationId}` }],
    },

    // List provider templates (what provider actually sees)
    getV2AdminNotetemplatesProviderByProviderId: {
      providesTags: (result, _error, { providerId }) =>
        result
          ? [
              ...(result.templates?.map(t => ({ type: 'NoteTemplate' as const, id: t.template.id })) || []),
              { type: 'NoteTemplateList' as const, id: `PROVIDER_${providerId}` },
            ]
          : [{ type: 'NoteTemplateList' as const, id: `PROVIDER_${providerId}` }],
    },

    // Create org template
    postV2AdminNotetemplatesOrgByOrganizationId: {
      invalidatesTags: (result, _error, { organizationId }) => [
        { type: 'NoteTemplateList', id: `ORG_${organizationId}` },
        { type: 'NoteTemplateList', id: 'ALL' },
        ...(result?.template ? [{ type: 'NoteTemplate' as const, id: result.template.id }] : []),
      ],
    },

    // Copy template to user
    postV2AdminNotetemplatesCopy: {
      invalidatesTags: (result, _error, { adminCopyNoteTemplateRequest }) => [
        { type: 'NoteTemplateList', id: `USER_${adminCopyNoteTemplateRequest.target_user_id}` },
        { type: 'NoteTemplateList', id: 'ALL' },
        ...(result?.template
          ? [{ type: 'NoteTemplate' as const, id: result.template.id }]
          : []),
        ...(adminCopyNoteTemplateRequest.target_organization_id
          ? [{ type: 'NoteTemplateList' as const, id: `ORG_${adminCopyNoteTemplateRequest.target_organization_id}` }]
          : []),
      ],
    },

    // Convert user template to org
    postV2AdminNotetemplatesConvertToOrg: {
      invalidatesTags: (result, _error, { adminConvertToOrgRequest }) => [
        { type: 'NoteTemplateList', id: `ORG_${adminConvertToOrgRequest.organization_id}` },
        { type: 'NoteTemplateList', id: 'ALL' },
        { type: 'NoteTemplate', id: adminConvertToOrgRequest.template_id },
        ...(result?.template
          ? [{ type: 'NoteTemplate' as const, id: result.template.id }]
          : []),
      ],
    },

    // Remove template from user's list
    deleteV2AdminNotetemplatesUserByUserIdTemplatesAndTemplateId: {
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'NoteTemplateList', id: `PROVIDER_${userId}` },
        { type: 'NoteTemplateList', id: `USER_${userId}` },
      ],
    },

    // Delete template
    deleteV2AdminNotetemplatesByTemplateId: {
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'NoteTemplateList', id: 'ALL' },
        { type: 'NoteTemplate', id: templateId },
        // Note: We can't invalidate user/org specific lists without knowing the scope
        // The backend should handle this, but we invalidate all lists to be safe
        { type: 'NoteTemplateList', id: 'USER_*' },
        { type: 'NoteTemplateList', id: 'ORG_*' },
      ],
    },

    // Get single template for viewing/editing
    getV2AdminNotetemplatesByTemplateId: {
      providesTags: (_result, _error, { templateId }) => [
        { type: 'NoteTemplate' as const, id: templateId },
      ],
    },

    // Update template (partial update)
    putV2AdminNotetemplatesByTemplateId: {
      invalidatesTags: (result, _error, { templateId }) => [
        { type: 'NoteTemplate', id: templateId },
        { type: 'NoteTemplateList', id: 'ALL' },
        // Invalidate related lists based on scope changes
        ...(result?.template?.organization_id
          ? [{ type: 'NoteTemplateList' as const, id: `ORG_${result.template.organization_id}` }]
          : []),
        ...(result?.template?.user_id
          ? [{ type: 'NoteTemplateList' as const, id: `USER_${result.template.user_id}` }]
          : []),
      ],
    },

    // Fork template for user
    postV2AdminNotetemplatesForkForUser: {
      invalidatesTags: (result, _error, { adminForkTemplateForUserRequest }) => [
        { type: 'NoteTemplateList', id: `USER_${adminForkTemplateForUserRequest.target_user_id}` },
        { type: 'NoteTemplateList', id: 'ALL' },
        ...(result?.template ? [{ type: 'NoteTemplate' as const, id: result.template.id }] : []),
        ...(adminForkTemplateForUserRequest.target_organization_id
          ? [{ type: 'NoteTemplateList' as const, id: `ORG_${adminForkTemplateForUserRequest.target_organization_id}` }]
          : []),
      ],
    },
  },
});

// Export hooks with user-friendly names
export const {
  useGetV2AdminNotetemplatesQuery: useGetAdminNoteTemplatesQuery,
  useLazyGetV2AdminNotetemplatesQuery: useLazyGetAdminNoteTemplatesQuery,
  useGetV2AdminNotetemplatesUserByUserIdQuery: useGetAdminUserNoteTemplatesQuery,
  useLazyGetV2AdminNotetemplatesUserByUserIdQuery: useLazyGetAdminUserNoteTemplatesQuery,
  useGetV2AdminNotetemplatesOrgByOrganizationIdQuery: useGetAdminOrgNoteTemplatesQuery,
  useLazyGetV2AdminNotetemplatesOrgByOrganizationIdQuery: useLazyGetAdminOrgNoteTemplatesQuery,
  usePostV2AdminNotetemplatesOrgByOrganizationIdMutation: useCreateAdminOrgNoteTemplateMutation,
  usePostV2AdminNotetemplatesCopyMutation: useCopyAdminNoteTemplateMutation,
  usePostV2AdminNotetemplatesConvertToOrgMutation: useConvertAdminNoteTemplateToOrgMutation,
  useDeleteV2AdminNotetemplatesByTemplateIdMutation: useDeleteAdminNoteTemplateMutation,
  // Single template GET/PUT for editing
  useGetV2AdminNotetemplatesByTemplateIdQuery: useGetAdminNoteTemplateQuery,
  useLazyGetV2AdminNotetemplatesByTemplateIdQuery: useLazyGetAdminNoteTemplateQuery,
  usePutV2AdminNotetemplatesByTemplateIdMutation: useUpdateAdminNoteTemplateMutation,
  // Fork template for user
  usePostV2AdminNotetemplatesForkForUserMutation: useForkTemplateForUserMutation,
  // Remove template from user's list
  useDeleteV2AdminNotetemplatesUserByUserIdTemplatesAndTemplateIdMutation: useRemoveUserTemplateMutation,
  // Provider templates (what provider actually sees)
  useGetV2AdminNotetemplatesProviderByProviderIdQuery: useGetAdminProviderTemplatesQuery,
} = noteTemplatesAdminApi;

// Re-export types for convenience
export type {
  AdminNoteTemplateInfo,
  AdminNoteTemplateListResponse,
  AdminCopyNoteTemplateResponse,
  AdminConvertToOrgRequest,
  AdminCopyNoteTemplateRequest,
  AdminCreateOrgTemplateRequest,
  AdminDeleteTemplateResponse,
  NoteTemplateOwnerScope,
  // Types for single template GET/PUT
  AdminGetNoteTemplateResponse,
  AdminUpdateNoteTemplateResponse,
  AdminUpdateNoteTemplateRequest,
  AdminNoteTemplateSectionRequest,
  NoteTemplate2,
  NoteTemplateRevision2,
  NoteTemplateSection2,
  // Types for fork-for-user
  AdminForkTemplateForUserRequest,
  AdminForkTemplateForUserResponse,
  // Types for provider templates
  EffectiveNoteTemplateSummary,
  AdminProviderTemplateListResponse,
} from '@store/generated/templates';

